import React, { useMemo } from "react";
import { Form } from "antd";
import SkeletonTable from "../../components/table/SkeletonTable";
import LoadableButton from "../../components/button/LoadableButton";
import UserAvatar from "../../components/common/UserAvatar";
import columns from "../../columns/License/columns";
import ModalComponent from "../../components/global/modals/ModalComponent";
import Plus from "../../assets/Plus";
import LeftArrow from "../../assets/LeftArrow";
import ConfirmationModal from "../../components/global/modals/ConfirmationModal";
import AdminWiseEmployeeModal from "./AdminWiseEmployeeModal";
import LicenseFormFields from "./components/LicenseFormFields";
import LicenseActionButtons from "./components/LicenseActionButtons";
import useLicense from "./useLicense";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import UpgradePlanModal from "./components/UpgradePlanModal";
import LicenseHistoryModal from "./components/LicenseHistoryModal";

const License = () => {
  const {
    form,
    isLoading,
    data,
    currentView,
    isLoadingLicense,
    fileList,
    previewImage,
    previewOpen,
    planOptions,
    planList,
    isEditModalOpen,
    isUpgradeModalOpen,
    setIsUpgradeModalOpen,
    upgradeLicenseData,
    isUpgradeLoading,
    countryDetails,
    isLicenseDelete,
    isDeleteLoading,
    showEmployeeModal,
    selectedEmployee,
    initialValues,
    setCurrentView,
    setPreviewOpen,
    setPreviewImage,
    setIsEditModalOpen,
    setIsLicenseDelete,
    setShowEmployeeModal,
    handleChange,
    handlePreview,
    handleAddLicense,
    handleEditLicense,
    deleteItemHandler,
    handleAdminLogin,
    openEditModal,
    openDeleteModal,
    openEmployeeModal,
    openClientList,
    openUpgradeModal,
    handleUpgradeSubmit,
    openHistoryModal,
    isHistoryModalOpen,
    setIsHistoryModalOpen,
    historyLicenseId,
    paymentTypes,
  } = useLicense();

  const dataSource = useMemo(() => {
    return data?.map((item) => ({
      ...item,
      userDetails: <UserAvatar data={item} />,
      businessLogo: (
        <p
          className={`hover:underline ${
            item?.license?.logo
              ? "text-blue-500 cursor-pointer"
              : "cursor-not-allowed text-gray-400"
          }`}
          onClick={() => {
            if (item?.license?.logo) {
              window.open(item.license.logo, "_blank");
            }
          }}
        >
          View
        </p>
      ),
      domainName: item?.license?.domain_name,
      url: (
        <div className="flex flex-col">
          <a
            href={item?.license?.admin_portal_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${
              item?.license?.admin_portal_url
                ? ""
                : "cursor-not-allowed text-gray-400"
            }`}
          >
            Backoffice
          </a>
          <a
            href={item?.license?.client_portal_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${
              item?.license?.admin_portal_url
                ? ""
                : "cursor-not-allowed text-gray-400"
            }`}
          >
            Cabinet
          </a>
        </div>
      ),
      licenseName: item?.license?.license_name,
      created_at: item?.created_at
        ? moment(item?.created_at).format("lll")
        : "--",
      action: (
        <LicenseActionButtons
          item={item}
          onEdit={openEditModal}
          onClientList={openClientList}
          onEmployeeList={openEmployeeModal}
          onAdminLogin={handleAdminLogin}
          onUpgrade={openUpgradeModal}
          onHistory={openHistoryModal}
          onDelete={openDeleteModal}
        />
      ),
    }));
  }, [
    data,
    openEditModal,
    openClientList,
    openEmployeeModal,
    handleAdminLogin,
    openUpgradeModal,
    openHistoryModal,
    openDeleteModal,
  ]);

  const navigate = useNavigate();

  return (
    <>
      {currentView === "default" ? (
        // ---------- Table View ----------
        <div className="border bg-white rounded-xl py-3">
          <div className="flex items-center justify-between sm:px-4 max-sm:px-2 mb-1">
            <h2 className="font-semibold text-xl">Manage License</h2>
            <button
              className="flex items-center gap-2 m-2 px-4 py-2 bg-secondary text-white rounded-md max-sm:px-1 max-sm:py-1"
              onClick={() => setCurrentView("add")}
            >
              <Plus className="w-5 h-5" />
              <span className="max-sm:text-xs">Add License</span>
            </button>
          </div>
          <SkeletonTable
            columns={columns}
            data={dataSource}
            isLoading={isLoadingLicense}
            pagination={true}
          />
        </div>
      ) : (
        // ---------- Add License Page ----------
        <div className="border bg-White rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div
              className="cursor-pointer"
              onClick={() => {
                form.resetFields();
                // setCurrentView("default");
                navigate("/license", { state: { status: "default" } });
              }}
            >
              <LeftArrow />
            </div>
            <p className="title">Add License</p>
          </div>

          <Form
            autoComplete="off"
            form={form}
            initialValues={initialValues}
            layout="vertical"
            onFinish={handleAddLicense}
          >
            <LicenseFormFields
              isEdit={false}
              countryDetails={countryDetails}
              planOptions={planOptions}
              fileList={fileList}
              onPreview={handlePreview}
              onChange={handleChange}
              previewImage={previewImage}
              previewOpen={previewOpen}
              setPreviewOpen={setPreviewOpen}
              setPreviewImage={setPreviewImage}
              paymentTypes={paymentTypes}
            />

            <div className="flex justify-center">
              <LoadableButton
                className="btn-primary"
                type="submit"
                lable="Confirm"
                loadingLable="Creating License..."
                isLoading={isLoading}
              />
            </div>
          </Form>
        </div>
      )}

      {/* ---------- Edit License Modal ---------- */}
      <ModalComponent
        isOpen={isEditModalOpen}
        setIsOpen={setIsEditModalOpen}
        title="Edit License"
        width={1000}
      >
        <Form
          autoComplete="off"
          form={form}
          layout="vertical"
          onFinish={handleEditLicense}
        >
          <LicenseFormFields
            isEdit={true}
            countryDetails={countryDetails}
            planOptions={planOptions}
            fileList={fileList}
            onPreview={handlePreview}
            onChange={handleChange}
            previewImage={previewImage}
            previewOpen={previewOpen}
            setPreviewOpen={setPreviewOpen}
            setPreviewImage={setPreviewImage}
            paymentTypes={paymentTypes}
          />

          <div className="flex justify-center">
            <LoadableButton
              className="btn-primary"
              type="submit"
              lable="Update"
              loadingLable="Updating License..."
              isLoading={isLoading}
            />
          </div>
        </Form>
      </ModalComponent>

      {/* ---------- Delete License Modal ---------- */}
      <ConfirmationModal
        isOpen={isLicenseDelete}
        setIsOpen={setIsLicenseDelete}
        message={`Are you sure you want to permanently delete this License?`}
        onConfirm={deleteItemHandler}
        isLoading={isDeleteLoading}
        loadingLabel={`Deleting License...`}
      />

      <AdminWiseEmployeeModal
        isOpen={showEmployeeModal}
        setIsOpen={setShowEmployeeModal}
        selectedAdmin={selectedEmployee}
      />

      {/* ---------- Upgrade Plan Modal ---------- */}
      <UpgradePlanModal
        isOpen={isUpgradeModalOpen}
        setIsOpen={setIsUpgradeModalOpen}
        plans={planList}
        onSubmit={handleUpgradeSubmit}
        isLoading={isUpgradeLoading}
      />

      {/* ---------- License History Modal ---------- */}
      <LicenseHistoryModal
        isOpen={isHistoryModalOpen}
        setIsOpen={setIsHistoryModalOpen}
        licenseId={historyLicenseId}
      />
    </>
  );
};

export default License;
